import { useRef, useState, useEffect } from 'react';
import AuthService from '../../services/authService';
import { apiErrToMessage } from '../../utils/apiError';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import * as XLSX from 'xlsx';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import './NaverIntegratedRank.css';
import {
    Card,
    Row,
    Col,
    Button,
    Typography,
    Space,
    Select,
    Input,
    List,
    Tag,
    Spin,
    Divider,
    Empty,
    Popover,
    message,
} from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    PlusOutlined,
    ReloadOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

const NaverIntegratedRank = ({ title: _title = '키워드 통합검색 순위 조회' }) => {
    const buttonGroupRef = useRef(null);
    const [showFloating, setShowFloating] = useState(false);

    //const MAX_ROWS = 30; // 임시 상한값 (enum 대체 예정)
    const [loading, setLoading] = useState(false);

    const [inputRows, setInputRows] = useState([{ keyword: '', mid: '' }]);
    // const [inputRows, setInputRows] = useState([
    //     { keyword: '코너선반라운드형', mid: '83014360386' },
    //     { keyword: '엘지 공기청정기 필터', mid: '82124552532' },
    //     { keyword: '엘지 공기청정기 필터', mid: '82124552532' },
    //     { keyword: '엘지 공기청정기 필터', mid: '82124552532' },
    //     { keyword: '엘지 공기청정기 필터', mid: '82124552532' },
    //     { keyword: '강아지사료', mid: '89058350376' },
    //     { keyword: '강아지사료', mid: '89058350376' },
    //     { keyword: '강아지사료', mid: '89058358744' },
    //     { keyword: '강아지사료', mid: '89058358744' },
    //     { keyword: '보조배터리', mid: '89150142198' },
    //     { keyword: '보조배터리', mid: '89150142198' },
    // ]);
    const [pastedText, setPastedText] = useState('');
    const [results, setResults] = useState([]);

    const [inputErrors, setInputErrors] = useState([]);

    const [delimiter, setDelimiter] = useState('tab'); // 기본은 tab

    // 쿼터(사용량)
    const [_quota, setQuota] = useState({
        used: 0,
        limit: 0,
        bonus: 0,
        remaining: 0,
        day: '',
        loading: true,
        error: null,
    });

    const handleExportExcel = () => {
        if (results.length === 0) {
            message.warning('저장할 결과가 없습니다.');
            return;
        }

        // ✅ 엑셀에 넣을 데이터 구성
        const exportData = results.map(r => {
            const proxyData = r.pMResults?.[0];
            const checks = proxyData?.checks || {};

            const adRank = checks?.productLinkPosition?.adRank || '';
            const normalRank = checks?.productLinkPosition?.normalRank || '';

            return {
                키워드: r.keyword || '',
                MID: r.productMid || '',
                '통합검색 광고 순위': adRank ? `${adRank}` : '',
                '통합검색 일반 순위': normalRank ? `${normalRank}` : '',
            };
        });

        // ✅ 워크북 생성
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '통합검색결과');

        // ✅ 파일 다운로드
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 19).replace(/[-T:]/g, '');
        const filename = `키워드통합검색순위_${timestamp}.xlsx`;

        XLSX.writeFile(workbook, filename);
    };

    // ✅ 검증 함수
    const validateInputs = () => {
        const errors = inputRows.map(row => {
            const hasKeyword = row.keyword.trim().length > 0;
            const isNumeric = /^[0-9]+$/.test(row.mid.trim());
            const hasMid = row.mid.trim().length > 0;

            return !(hasKeyword && hasMid && isNumeric);
        });

        setInputErrors(errors);

        if (errors.includes(true)) {
            message.warning(
                '입력값을 다시 확인해주세요. (빈칸 또는 숫자가 아닌 상품번호가 있습니다.)'
            );
            return false;
        }

        return true;
    };

    // ✅ 행 삭제 (마지막 한 줄은 남기기)
    const handleDeleteRow = index => {
        setInputRows(prev => {
            if (prev.length <= 1) return prev; // 마지막 행이면 삭제 X
            return prev.filter((_, i) => i !== index);
        });
    };

    const handlePaste = e => {
        e.preventDefault();

        const htmlData = e.clipboardData.getData('text/html');
        const plainData = e.clipboardData.getData('text/plain');
        let text = '';

        if (htmlData && htmlData.includes('<table')) {
            const div = document.createElement('div');
            div.innerHTML = htmlData;

            const rows = [...div.querySelectorAll('tr')];
            text = rows
                .map(row => {
                    const cells = [...row.querySelectorAll('td, th')]
                        .map(td =>
                            td.innerText
                                .replace(/\r?\n+/g, ' ') // 셀 내부 줄바꿈 → 공백
                                .replace(/\s+/g, ' ') // 다중 공백 정리
                                .trim()
                        )
                        .filter(v => v.length > 0); // ✅ 비어있는 셀 제거

                    // ✅ 첫 번째 = 키워드, 두 번째 = MID (그 이상은 무시)
                    const keyword = cells[0] || '';
                    const mid = cells[1] || '';
                    return `${keyword}\t${mid}`.trim();
                })
                .filter(line => line.trim().length > 0)
                .join('\n');
        } else {
            // ✅ 일반 텍스트 (메모장, VSCode 등)
            text = plainData.replace(/\r/g, '').trim();
        }

        // ✅ 불필요한 개행 제거
        text = text.replace(/^\s*\n+|\n+\s*$/g, '');

        // ✅ 구분자 적용
        let splitPattern;
        switch (delimiter) {
            case 'comma':
                splitPattern = /,/;
                break;
            case 'space':
                splitPattern = /\s+/;
                break;
            case 'tab':
            default:
                splitPattern = /\t/;
        }

        // ✅ 실제 데이터 변환
        const newLines = text
            .split(/\r?\n|;/)
            .map(line => line.trim())
            .filter(line => line.length > 0)
            .map(line => {
                const parts = line.split(splitPattern);
                return {
                    keyword: parts[0]?.trim() || '',
                    mid: parts[1]?.trim() || '',
                };
            });

        // ✅ 기존 merge 로직
        setPastedText(text);
        setInputRows(prev => {
            const isInitialEmpty =
                prev.length === 1 && prev[0].keyword.trim() === '' && prev[0].mid.trim() === '';

            let merged;
            if (isInitialEmpty) merged = newLines;
            else {
                merged = [...prev];
                newLines.forEach(n => {
                    const exists = merged.some(
                        p => p.keyword.trim() === n.keyword.trim() && p.mid.trim() === n.mid.trim()
                    );
                    if (!exists) merged.push(n);
                });
            }

            if (merged.length > maxRows) {
                message.warning(`최대 ${maxRows}개까지만 가능합니다.`);
                return merged.slice(0, maxRows);
            }

            return merged;
        });
    };

    const _handlePaste3 = e => {
        e.preventDefault();

        const htmlData = e.clipboardData.getData('text/html');
        const plainData = e.clipboardData.getData('text/plain');

        let text = '';

        if (htmlData.includes('<table')) {
            // ✅ 테이블 구조에서 td 값 추출 시, 내부 줄바꿈 제거
            const div = document.createElement('div');
            div.innerHTML = htmlData;

            const rows = [...div.querySelectorAll('tr')];
            text = rows
                .map(
                    row =>
                        [...row.querySelectorAll('td')]
                            .map(td =>
                                td.innerText
                                    .replace(/\r?\n+/g, ' ') // ✅ 셀 내부 줄바꿈 제거 (공백으로 대체)
                                    .replace(/\s+/g, ' ') // ✅ 다중 공백 정리
                                    .trim()
                            )
                            .join('\t') // ✅ 각 셀은 탭으로 구분
                )
                .join('\n'); // ✅ 각 행은 개행으로 구분
        } else {
            // ✅ 일반 텍스트
            text = plainData.replace(/\r/g, '').trim();
        }

        // ✅ 이제 기존 로직 그대로 이어가기
        text = text.replace(/^\s*\n+|\n+\s*$/g, '');
        let splitPattern;
        switch (delimiter) {
            case 'comma':
                splitPattern = /,/;
                break;
            case 'space':
                splitPattern = /\s+/;
                break;
            case 'tab':
            default:
                splitPattern = /\t/;
        }

        const newLines = text
            .split(/\r?\n|;/)
            .map(line => line.trim())
            .filter(line => line.length > 0)
            .map(line => {
                const parts = line.split(splitPattern);
                return { keyword: parts[0]?.trim() || '', mid: parts[1]?.trim() || '' };
            });

        // 이후 기존 merge 처리 로직 유지
        setPastedText(text);
        setInputRows(prev => {
            const isInitialEmpty =
                prev.length === 1 && prev[0].keyword.trim() === '' && prev[0].mid.trim() === '';

            let merged;
            if (isInitialEmpty) merged = newLines;
            else {
                merged = [...prev];
                newLines.forEach(n => {
                    const exists = merged.some(
                        p => p.keyword.trim() === n.keyword.trim() && p.mid.trim() === n.mid.trim()
                    );
                    if (!exists) merged.push(n);
                });
            }

            if (merged.length > maxRows) {
                message.warning(`최대 ${maxRows}개까지만 가능합니다.`);
                return merged.slice(0, maxRows);
            }

            return merged;
        });
    };

    const _handlePaste2 = e => {
        e.preventDefault();

        let text = '';
        const htmlData = e.clipboardData.getData('text/html');
        const plainData = e.clipboardData.getData('text/plain');

        if (htmlData) {
            const div = document.createElement('div');
            div.innerHTML = htmlData;
            let cleaned = htmlData
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/tr>/gi, '\n')
                .replace(/<\/p>/gi, '\n')
                .replace(/<\/td>/gi, '\t')
                .replace(/&nbsp;/gi, ' ')
                .replace(/<!--[\s\S]*?-->/g, '')
                .replace(/<[^>]*>/g, '')
                .trim();
            text = cleaned;
        } else {
            // ✅ 모든 캐리지리턴 제거 + trim으로 개행 제거
            text = plainData.replace(/\r/g, '').trim();
        }

        // ✅ 붙여넣기 첫줄/마지막줄 불필요한 개행 제거
        text = text.replace(/^\s*\n+|\n+\s*$/g, '');

        // ✅ 선택된 구분자 적용
        let splitPattern;
        switch (delimiter) {
            case 'comma':
                splitPattern = /,/;
                break;
            case 'space':
                splitPattern = /\s+/;
                break;
            case 'tab':
            default:
                splitPattern = /\t/;
        }

        // ✅ 빈 줄 완전 제거
        const newLines = text
            .split(/\r?\n|;/)
            .map(line => line.trim())
            .filter(line => line.length > 0)
            .map(line => {
                const parts = line.split(splitPattern);
                return { keyword: parts[0]?.trim() || '', mid: parts[1]?.trim() || '' };
            });

        setPastedText(text);

        setInputRows(prev => {
            const isInitialEmpty =
                prev.length === 1 && prev[0].keyword.trim() === '' && prev[0].mid.trim() === '';

            let merged;
            if (isInitialEmpty) {
                merged = newLines;
            } else {
                merged = [...prev];
                newLines.forEach(n => {
                    const exists = merged.some(
                        p => p.keyword.trim() === n.keyword.trim() && p.mid.trim() === n.mid.trim()
                    );
                    if (!exists) merged.push(n);
                });
            }

            // ✅ 최대 300개 제한
            if (merged.length > maxRows) {
                message.warning(`최대 ${maxRows}개까지만 가능합니다.`);
                return merged.slice(0, maxRows);
            }

            return merged;
        });
    };

    // ✅ 수동 입력 핸들러
    const handleChange = (index, field, value) => {
        const updated = [...inputRows];
        updated[index][field] = value;
        setInputRows(updated);
    };

    const handleSearch = async () => {
        if (loading) return; // ✅ 중복 요청 방지
        if (!validateInputs()) return;

        setLoading(true); // ✅ 로딩 시작
        setResults([]);

        try {
            const { data } = await AuthService.authAxios.post('/naver/v2', {
                items: inputRows.map(r => ({
                    keyword: r.keyword.trim(),
                    productMid: r.mid.trim(),
                })),
                includePriceCompare: 1,
            });

            console.log('✅ 서버 응답:', data);

            // ✅ quota 반영
            applyQuotaIfAny(setQuota, data);

            if (!data.success) {
                message.error(apiErrToMessage(data));
                return;
            }

            setResults(data.results);
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            message.error(apiErrToMessage(data, status));
        } finally {
            setLoading(false); // ✅ 로딩 종료
        }
    };

    // ✅ 새로운 행 추가
    const addRow = () => {
        if (inputRows.length >= maxRows) {
            message.warning(`최대 ${maxRows}개까지만 가능합니다.`);
            return;
        }
        setInputRows([...inputRows, { keyword: '', mid: '' }]);
    };

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await AuthService.authAxios.get(
                    '/naver/quota/status/naverIntegratedRank',
                    {
                        params: { ensureRow: 1 },
                    }
                );
                if (!alive) return;
                if (data?.ok) {
                    const { used, limit, bonus, remaining, day } = data;
                    setQuota({ used, limit, bonus, remaining, day, loading: false, error: null });
                } else {
                    setQuota(q => ({ ...q, loading: false, error: '상태 조회 실패' }));
                }
            } catch (err) {
                setQuota(q => ({ ...q, loading: false, error: err?.message ?? '에러' }));
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    const applyQuotaIfAny = (setQuota, obj) => {
        if (!obj || typeof obj !== 'object') return;
        const has = k => Object.prototype.hasOwnProperty.call(obj, k);
        if (has('used') || has('limit') || has('bonus') || has('remaining') || has('day')) {
            const { used = 0, limit = 0, bonus = 0, remaining = 0, day = '' } = obj;
            setQuota(q => ({ ...q, used, limit, bonus, remaining, day }));
            dispatchQuotaUpdated('naverIntegratedRank');
        }
    };

    // ✅ 현재 유효한 행 개수 계산
    const validCount = inputRows.filter(r => r.keyword.trim() !== '' || r.mid.trim() !== '').length;

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setShowFloating(!entry.isIntersecting);
            },
            { threshold: 0.1 }
        );

        const el = buttonGroupRef.current;
        if (el) observer.observe(el);

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            if (el) observer.unobserve(el);
        };
    }, []);

    const [maxRows, setMaxRows] = useState(20);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await AuthService.authAxios.get('/settings/rank-limit', {
                    params: { gubun: 'naverIntegratedRank' },
                });
                if (data?.success) {
                    setMaxRows(data.max_rows);
                    console.log(`✅ MAX_ROWS = ${data.max_rows} (${data.source})`);
                } else {
                    setMaxRows(20);
                }
            } catch (err) {
                console.error('⚠️ 행 제한 조회 실패:', err);
                setMaxRows(20);
            }
        })();
    }, []);

    useEffect(() => {
        setInputRows(prev => (prev.length > maxRows ? prev.slice(0, maxRows) : prev));
    }, [maxRows]);

    return (
        <ProtectedMenuPage menuKey="naverIntegratedRank">
            <div className="naver-integrated-rank-page">
                <div className="page-header-container no-header-border">
                    <Card className="control-card" bodyStyle={{ padding: '8px 16px' }}>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Text type="secondary">
                                    키워드와 상품번호(MID)를 입력하거나 붙여넣기 후 조회하세요.
                                </Text>
                            </Col>
                        </Row>
                    </Card>
                </div>
                <div className="page-content-inner">
                    <Row gutter={24} align="stretch">
                        <Col xl={10} lg={12} md={24} style={{ marginBottom: '24px' }}>
                            <Card
                                title={
                                    <Space size="small">
                                        <span>입력 방식 선택 및 데이터 입력</span>
                                        <Popover
                                            content={
                                                <div
                                                    style={{
                                                        maxWidth: '300px',
                                                        fontSize: '13px',
                                                        lineHeight: '1.6',
                                                    }}
                                                >
                                                    키워드와 상품번호(MID)를 입력하거나 아래 영역에
                                                    직접 붙여넣으세요.
                                                </div>
                                            }
                                            trigger="hover"
                                            placement="top"
                                            mouseEnterDelay={0.15}
                                            mouseLeaveDelay={0.1}
                                        >
                                            <QuestionCircleOutlined className="naver-integrated-tooltip-icon" />
                                        </Popover>
                                    </Space>
                                }
                                bordered={false}
                                className="control-card naver-integrated-input-card"
                            >
                                <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
                                    <Col flex="auto">
                                        <Space>
                                            <Text strong>붙여넣기 구분자:</Text>
                                            <Select
                                                value={delimiter}
                                                onChange={setDelimiter}
                                                style={{ width: '130px' }}
                                                size="small"
                                            >
                                                <Option value="tab">탭 (Tab)</Option>
                                                <Option value="comma">쉼표 (,)</Option>
                                                <Option value="space">공백 (Space)</Option>
                                            </Select>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Tag color={validCount > maxRows ? 'red' : 'blue'}>
                                            {validCount} / {maxRows}
                                        </Tag>
                                    </Col>
                                </Row>

                                <div
                                    onPaste={handlePaste}
                                    style={{
                                        border: '1px dashed #d9d9d9',
                                        borderRadius: '8px',
                                        minHeight: '100px',
                                        padding: '12px',
                                        background: '#fafafa',
                                        marginBottom: '20px',
                                        cursor: 'text',
                                        fontSize: '14px',
                                        transition: 'all 0.3s',
                                    }}
                                    contentEditable
                                    suppressContentEditableWarning
                                    className="paste-area"
                                >
                                    {pastedText || (
                                        <Text type="secondary">여기에 붙여넣기 (Ctrl+V)</Text>
                                    )}
                                </div>

                                <Divider orientation="left" plain>
                                    상세 입력
                                </Divider>

                                <div
                                    style={{
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                        paddingRight: '4px',
                                    }}
                                >
                                    {inputRows.map((row, i) => (
                                        <Row key={i} gutter={8} style={{ marginBottom: '8px' }}>
                                            <Col span={10}>
                                                <Input
                                                    placeholder="키워드"
                                                    value={row.keyword}
                                                    onChange={e =>
                                                        handleChange(i, 'keyword', e.target.value)
                                                    }
                                                    status={
                                                        inputErrors[i] && row.keyword.trim() === ''
                                                            ? 'error'
                                                            : ''
                                                    }
                                                />
                                            </Col>
                                            <Col span={10}>
                                                <Input
                                                    placeholder="상품번호(MID)"
                                                    value={row.mid}
                                                    onChange={e =>
                                                        handleChange(i, 'mid', e.target.value)
                                                    }
                                                    status={
                                                        inputErrors[i] &&
                                                        (!/^[0-9]+$/.test(row.mid.trim()) ||
                                                            row.mid.trim() === '')
                                                            ? 'error'
                                                            : ''
                                                    }
                                                />
                                            </Col>
                                            <Col span={4}>
                                                <Button
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleDeleteRow(i)}
                                                    disabled={inputRows.length <= 1}
                                                    block
                                                />
                                            </Col>
                                        </Row>
                                    ))}
                                </div>

                                <div ref={buttonGroupRef} style={{ marginTop: '20px' }}>
                                    <Row justify="space-between" align="middle">
                                        <Col>
                                            <Space size="small">
                                                <Button
                                                    icon={<PlusOutlined />}
                                                    onClick={addRow}
                                                    className="page-action-btn"
                                                >
                                                    행 추가
                                                </Button>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => {
                                                        setInputRows([{ keyword: '', mid: '' }]);
                                                        setPastedText('');
                                                        setResults([]);
                                                    }}
                                                    className="page-action-btn"
                                                >
                                                    초기화
                                                </Button>
                                            </Space>
                                        </Col>
                                        <Col>
                                            <Button
                                                type="primary"
                                                icon={<SearchOutlined />}
                                                onClick={handleSearch}
                                                loading={loading}
                                                className="search-btn-premium"
                                                style={{ minWidth: '100px' }}
                                            >
                                                조회하기
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>
                            </Card>
                        </Col>

                        <Col xl={14} lg={12} md={24}>
                            <Card
                                title={
                                    <Row
                                        justify="space-between"
                                        align="middle"
                                        style={{ width: '100%' }}
                                    >
                                        <Col>조회 결과</Col>
                                        <Col>
                                            {results.length > 0 && (
                                                <Button
                                                    className="excel-button"
                                                    icon={<DownloadOutlined />}
                                                    onClick={handleExportExcel}
                                                    title="엑셀 다운로드"
                                                    size="small"
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                }
                                bordered={false}
                                className="naver-integrated-result-card"
                                bodyStyle={{ padding: '16px 24px' }}
                            >
                                {loading ? (
                                    <div style={{ textAlign: 'center', padding: '48px 0' }}>
                                        <Spin size="large" tip="순위 조회 중..." />
                                    </div>
                                ) : results.length === 0 ? (
                                    <Empty
                                        description="조회 결과가 없습니다."
                                        style={{ marginTop: '48px' }}
                                    />
                                ) : (
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={results}
                                        renderItem={(r, _i) => {
                                            const proxyData = r.pMResults?.[0];
                                            const checks = proxyData?.checks;
                                            const adRank = checks?.productLinkPosition?.adRank;
                                            const normalRank =
                                                checks?.productLinkPosition?.normalRank;

                                            return (
                                                <List.Item
                                                    extra={
                                                        checks?.hasPriceCompare && (
                                                            <div className="naver-integrated-rank-extra">
                                                                <CheckCircleOutlined
                                                                    style={{
                                                                        color: '#52c41a',
                                                                        marginRight: '6px',
                                                                    }}
                                                                />
                                                                <Text
                                                                    strong
                                                                    style={{ color: '#52c41a' }}
                                                                >
                                                                    통검가격비교탭
                                                                </Text>{' '}
                                                                {checks?.productLinkPosition !=
                                                                    null &&
                                                                (checks.productLinkPosition
                                                                    ?.adRank ||
                                                                    checks.productLinkPosition
                                                                        ?.normalRank) ? (
                                                                    <Text
                                                                        strong
                                                                        style={{ color: '#2e5bff' }}
                                                                    >
                                                                        (
                                                                        {checks.productLinkPosition
                                                                            ?.adRank
                                                                            ? `AD ${checks.productLinkPosition.adRank}위`
                                                                            : null}
                                                                        {checks.productLinkPosition
                                                                            ?.adRank &&
                                                                        checks.productLinkPosition
                                                                            ?.normalRank
                                                                            ? ' / '
                                                                            : ''}
                                                                        {checks.productLinkPosition
                                                                            ?.normalRank
                                                                            ? `${checks.productLinkPosition.normalRank}위`
                                                                            : null}
                                                                        )
                                                                    </Text>
                                                                ) : (
                                                                    <Text type="secondary">
                                                                        (순위 없음)
                                                                    </Text>
                                                                )}
                                                            </div>
                                                        )
                                                    }
                                                >
                                                    <List.Item.Meta
                                                        title={
                                                            <Space size="large">
                                                                <Text
                                                                    strong
                                                                    style={{ fontSize: '16px' }}
                                                                >
                                                                    {r.keyword}
                                                                </Text>
                                                                <Tag color="default">
                                                                    MID: {r.productMid}
                                                                </Tag>
                                                            </Space>
                                                        }
                                                        description={
                                                            !checks?.hasPriceCompare && (
                                                                <div style={{ marginTop: '4px' }}>
                                                                    {checks?.productLinkPosition ? (
                                                                        <Space
                                                                            split={
                                                                                <Divider type="vertical" />
                                                                            }
                                                                        >
                                                                            {adRank ? (
                                                                                <Text>
                                                                                    광고 순위:{' '}
                                                                                    <Text
                                                                                        strong
                                                                                        type="primary"
                                                                                    >
                                                                                        {adRank}위
                                                                                    </Text>
                                                                                </Text>
                                                                            ) : (
                                                                                <Text type="secondary">
                                                                                    광고 순위 없음
                                                                                </Text>
                                                                            )}
                                                                            {normalRank ? (
                                                                                <Text>
                                                                                    일반 순위:{' '}
                                                                                    <Text
                                                                                        strong
                                                                                        type="danger"
                                                                                    >
                                                                                        {normalRank}
                                                                                        위
                                                                                    </Text>
                                                                                </Text>
                                                                            ) : (
                                                                                <Text type="secondary">
                                                                                    일반 순위 없음
                                                                                </Text>
                                                                            )}
                                                                        </Space>
                                                                    ) : (
                                                                        <Text type="secondary">
                                                                            순위 정보가 없습니다.
                                                                        </Text>
                                                                    )}
                                                                </div>
                                                            )
                                                        }
                                                    />
                                                </List.Item>
                                            );
                                        }}
                                    />
                                )}
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            {showFloating && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '40px',
                        right: '40px',
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: '16px',
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 1000,
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center',
                    }}
                >
                    <Space size="small">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setInputRows([{ keyword: '', mid: '' }]);
                                setPastedText('');
                                setResults([]);
                            }}
                            className="page-action-btn"
                        >
                            초기화
                        </Button>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={loading}
                            className="search-btn-premium"
                        >
                            조회하기
                        </Button>
                    </Space>
                </div>
            )}
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { NaverIntegratedRank };
